import {
  BugIcon,
  Inbox,
  Loader2Icon,
  SearchIcon,
  ThumbsUp,
} from 'lucide-react';
import pluralize from 'pluralize';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { roles } from '@/modules/auth/roles';
import { hasAnyRole } from '@/modules/auth/utils/hasRoles';
import { CandidateList } from '@/modules/candidates/components/CandidateList';
import {
  CANDIDATE_JOBS_MAX_LIMIT,
  useCandidateJobsQuery,
} from '@/modules/candidates/queries/useCandidateJobsQuery';
import type {
  CandidateJobsQueryParams,
  CandidateStatusApi,
} from '@/modules/candidates/types';
import { useCompanyQuery } from '@/modules/companies/queries/useCompanyQuery';
import { CoordinationPreconditionsCheck } from '@/modules/coordination/components/CoordinationPreconditionsCheck';
import { CoordinationProcessesSection } from '@/modules/coordination/components/CoordinationProcessesSection';
import { JobDropdown } from '@/modules/jobs/components/JobDropdown';
import { SourcingStatusTag } from '@/modules/jobs/components/SourcingStatusTag';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { useDebouncedValue } from '@/modules/search/hooks/useDebouncedValue';
import { Button } from '@/modules/ui/components/button/Button';
import Container from '@/modules/ui/components/container/Container';
import { EmptyState } from '@/modules/ui/components/empty-state/EmptyState';
import { Input } from '@/modules/ui/components/input/Input';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/modules/ui/components/tabs/Tabs';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

function getStatusFilterEmptyCopy(statusParam: CandidateStatusApi | null): {
  title: string;
  description: string;
} {
  if (statusParam === null) {
    return {
      title: 'No candidates yet',
      description:
        'No candidates have been added to this job. When sourcing runs, they will appear under All and New first.',
    };
  }
  switch (statusParam) {
    case 'New':
      return {
        title: 'No new candidates',
        description:
          'There are no candidates in the New stage right now. New profiles appear here first when sourcing adds them.',
      };
    case 'Shortlisted':
      return {
        title: 'No shortlisted candidates',
        description:
          'No candidates are in the Shortlisted stage yet. Switch to All or New to see who is in the pipeline.',
      };
    case 'Contacted':
      return {
        title: 'No contacted candidates',
        description:
          'No candidates are in the Contacted stage for this job yet.',
      };
    case 'ContactStarted':
      return {
        title: 'No contact started candidates',
        description:
          'No candidates are in the Contact Started stage for this job yet.',
      };
    case 'Prescreened':
    case 'AiPrescreened':
      return {
        title: 'No pre-screened candidates',
        description:
          'No candidates have reached the pre-screened stage for this job yet.',
      };
    case 'AssessmentCompleted':
      return {
        title: 'No completed assessments',
        description:
          'No candidates are waiting for an assessment outcome for this job.',
      };
    case 'Rejected':
      return {
        title: 'No rejected candidates',
        description: 'No candidates are marked as rejected for this job.',
      };
    case 'Declined':
      return {
        title: 'No declined candidates',
        description: 'No candidates are marked as declined for this job.',
      };
    case 'Submitted':
      return {
        title: 'No submitted candidates',
        description: 'No candidates have been submitted for this job yet.',
      };
    case 'Interviewing':
      return {
        title: 'No interviewing candidates',
        description:
          'No candidates are in the Interviewing stage for this job yet.',
      };
    case 'Hired':
      return {
        title: 'No hired candidates',
        description: 'No candidates have been hired for this job yet.',
      };
    default:
      return {
        title: 'No candidates in this stage',
        description: 'No candidates match the selected status for this job.',
      };
  }
}

const JobCandidates = () => {
  const { id: jobId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get('status') as CandidateStatusApi | null;
  const searchQueryParam = searchParams.get('search') ?? '';
  const hmFeedbackParam = searchParams.get('hmFeedback') === 'true';

  const [searchInput, setSearchInput] = useDebouncedValue(
    searchQueryParam,
    (value) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (value.trim()) {
        newSearchParams.set('search', value);
      } else {
        newSearchParams.delete('search');
      }
      setSearchParams(newSearchParams);
    },
  );

  const statusFilters: CandidateJobsQueryParams = statusParam
    ? statusParam === 'Prescreened'
      ? { status: ['Prescreened', 'AiPrescreened'] as CandidateStatusApi[] }
      : { status: [statusParam] }
    : {};

  const updateFilters = (
    newFilters: Partial<Omit<CandidateJobsQueryParams, 'jobId'>>,
  ) => {
    const newSearchParams = new URLSearchParams(searchParams);

    if ('status' in newFilters) {
      const firstStatus = newFilters.status?.[0];
      if (firstStatus) {
        newSearchParams.set('status', firstStatus);
      } else {
        newSearchParams.delete('status');
      }
    }

    setSearchParams(newSearchParams);
  };

  const currentUserQuery = useCurrentUserQuery();
  const isSuperAdmin = hasAnyRole(
    [roles.SuperAdmin],
    currentUserQuery.data?.roles ?? [],
  );

  const jobQuery = useJobQuery(jobId!);
  const companyQuery = useCompanyQuery(jobQuery.data?.companyId || '', {
    enabled: !!jobQuery.data?.companyId,
  });

  const candidateJobsQuery = useCandidateJobsQuery({
    jobId,
    ...statusFilters,
    limit: CANDIDATE_JOBS_MAX_LIMIT,
  });

  const candidateJobsUnfilteredQuery = useCandidateJobsQuery(
    { jobId, limit: CANDIDATE_JOBS_MAX_LIMIT },
    { enabled: Boolean(jobId) && Boolean(statusParam) },
  );

  const candidatesFromApi = candidateJobsQuery.data?.items ?? [];
  const unfilteredCandidates = statusParam
    ? (candidateJobsUnfilteredQuery.data?.items ?? [])
    : candidatesFromApi;

  const searchFilteredCandidates = searchInput.trim()
    ? candidatesFromApi.filter((candidateJob) => {
        const query = searchInput.toLowerCase();
        const fullName =
          `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName}`.toLowerCase();
        return fullName.includes(query);
      })
    : candidatesFromApi;

  const candidates = hmFeedbackParam
    ? searchFilteredCandidates.filter(
        (candidateJob) =>
          (candidateJob.feedbacks?.hmFeedbacks?.length ?? 0) > 0,
      )
    : searchFilteredCandidates;

  const isSourcingInProgress = jobQuery.data?.sourcingState === 'InProgress';
  const isCandidatesLoading =
    candidateJobsQuery.isLoading ||
    (Boolean(statusParam) && candidateJobsUnfilteredQuery.isLoading);

  const showInitialSourcingEmptyState =
    isSourcingInProgress &&
    !isCandidatesLoading &&
    unfilteredCandidates.length === 0 &&
    (statusParam === null || statusParam === 'New');

  const showSearchEmpty =
    Boolean(searchInput.trim()) &&
    candidates.length === 0 &&
    candidatesFromApi.length > 0;

  const showStatusFilterEmpty =
    !isCandidatesLoading &&
    !showInitialSourcingEmptyState &&
    !showSearchEmpty &&
    candidates.length === 0;

  const pageTitle = jobQuery.data?.title ?? 'Job Candidates';

  const showJobHeaderSkeleton =
    Boolean(jobId) && !jobQuery.data && jobQuery.isPending;

  const isNewFilterActive = statusParam === 'New';

  return (
    <>
      <DocumentMetadata
        title={showJobHeaderSkeleton ? 'Loading job…' : pageTitle}
      />

      <Container>
        <PageHeader
          title={
            showJobHeaderSkeleton ? (
              <span className="flex flex-wrap items-center gap-3">
                <Skeleton className="h-8 w-[min(100%,18rem)] max-w-full" />
                <Skeleton className="h-6 w-24 rounded-full" />
              </span>
            ) : (
              <span className="flex flex-wrap items-center gap-3">
                <span>{pageTitle}</span>
                {jobQuery.data?.sourcingState && (
                  <SourcingStatusTag
                    status={jobQuery.data.sourcingState}
                    lastSourcing={jobQuery.data.lastSourcing}
                  />
                )}
              </span>
            )
          }
          subtitle={
            showJobHeaderSkeleton ? (
              <Skeleton className="h-5 w-40" />
            ) : (
              jobQuery.data?.company && (
                <Link
                  to={`/companies/${jobQuery.data.company.id}/edit`}
                  className="hover:underline"
                >
                  {jobQuery.data.company.name}
                </Link>
              )
            )
          }
          action={
            <div className="flex items-center gap-2">
              {isSuperAdmin && jobId && (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/debug/job-sourcing/${jobId}`}>
                    <BugIcon className="size-4" />
                    Debug
                  </Link>
                </Button>
              )}
              {jobQuery.data && <JobDropdown job={jobQuery.data} />}
            </div>
          }
        />

        {jobQuery.data?.isTrackingCoordinationEvents && jobId && (
          <div className="pb-6">
            <CoordinationPreconditionsCheck jobId={jobId} />
          </div>
        )}

        {jobQuery.data?.isTrackingCoordinationEvents && jobId && (
          <div className="pb-6">
            <CoordinationProcessesSection jobId={jobId} />
          </div>
        )}

        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <SearchIcon className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                  <Input
                    placeholder="Search candidates..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="h-9 w-full pl-9 sm:w-50"
                  />
                </div>
                <Button
                  variant={hmFeedbackParam ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    const newSearchParams = new URLSearchParams(searchParams);
                    if (hmFeedbackParam) {
                      newSearchParams.delete('hmFeedback');
                    } else {
                      newSearchParams.set('hmFeedback', 'true');
                    }
                    setSearchParams(newSearchParams);
                  }}
                  className="flex items-center gap-1.5"
                >
                  <ThumbsUp className="size-4" />
                  HM Feedback
                </Button>
              </div>

              <Paragraph
                className={cn(
                  'text-muted-foreground',
                  isCandidatesLoading && 'min-h-5',
                )}
              >
                {isCandidatesLoading ? (
                  <Skeleton
                    aria-hidden
                    className="inline-block h-5 w-36 align-middle"
                  />
                ) : (
                  <>
                    {candidates.length}{' '}
                    {pluralize('candidate', candidates.length)}
                  </>
                )}
              </Paragraph>
            </div>

            <Tabs
              value={statusParam || 'All'}
              onValueChange={(value) =>
                updateFilters({
                  status:
                    value === 'All' ? undefined : [value as CandidateStatusApi],
                })
              }
            >
              <TabsList>
                <TabsTrigger value="All">All</TabsTrigger>
                <TabsTrigger value="New">New</TabsTrigger>
                <TabsTrigger value="Shortlisted">Shortlisted</TabsTrigger>
                <TabsTrigger value="ContactStarted">
                  Contact Started
                </TabsTrigger>
                <TabsTrigger value="Contacted">Contacted</TabsTrigger>
                <TabsTrigger value="Prescreened">Pre-screened</TabsTrigger>
                <TabsTrigger value="AssessmentCompleted">
                  Assessment completed
                </TabsTrigger>
                <TabsTrigger value="Submitted">Submitted</TabsTrigger>
                <TabsTrigger value="Interviewing">Interviewing</TabsTrigger>
                <TabsTrigger value="Hired">Hired</TabsTrigger>
                <TabsTrigger value="Rejected">Rejected</TabsTrigger>
                <TabsTrigger value="Declined">Declined</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {isCandidatesLoading ? (
            <div aria-busy aria-live="polite" className="relative">
              <span className="sr-only">
                Loading candidates and updating the total count.
              </span>
              <CandidateList
                jobId={jobId!}
                candidates={candidates}
                isLoading
                showCandidateBackgroundWhenLoading={isNewFilterActive}
                hiringStages={companyQuery.data?.hiringStages ?? []}
                job={jobQuery.data}
              />
            </div>
          ) : showInitialSourcingEmptyState ? (
            <EmptyState
              icon={
                <span
                  className="bg-muted/80 ring-border/60 flex size-16 items-center justify-center rounded-full shadow-sm ring-1"
                  aria-hidden
                >
                  <Loader2Icon className="text-primary size-8 animate-spin motion-reduce:animate-none" />
                </span>
              }
              title="Sourcing in progress"
              description="We're sourcing in real time. Candidates will appear here one by one as we find them."
            />
          ) : showSearchEmpty ? (
            <EmptyState
              icon={<SearchIcon className="text-muted-foreground size-12" />}
              title="No matching candidates"
              description="Try a different search or clear the search box to see everyone in this list."
            />
          ) : showStatusFilterEmpty ? (
            <EmptyState
              icon={<Inbox className="text-muted-foreground size-12" />}
              {...getStatusFilterEmptyCopy(statusParam)}
            />
          ) : (
            <div aria-live="polite" className="relative">
              <CandidateList
                jobId={jobId!}
                candidates={candidates}
                isLoading={false}
                showCandidateBackgroundWhenLoading={isNewFilterActive}
                hiringStages={companyQuery.data?.hiringStages ?? []}
                job={jobQuery.data}
              />
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

export default JobCandidates;
